/**
 * PageRegistry - A component registry for page-level components
 * 
 * This registry breaks circular dependencies by allowing components to be
 * registered at runtime instead of via static imports.
 * 
 * Usage:
 * - Page.jsx registers Body with: PageRegistry.register('Body', Body)
 * - Types.jsx retrieves it with: PageRegistry.get('Body')
 */

const registry = new Map();

const PageRegistry = {
    /**
     * Register a component with the registry
     * @param {string} key - The unique identifier for the component
     * @param {React.Component|Function} component - The component to register
     */
    register: (key, component) => {
        if (registry.has(key)) {
            console.warn(`PageRegistry: Component with key '${key}' is already registered. Overwriting.`);
        }
        registry.set(key, component);
    },

    /**
     * Get a component from the registry
     * @param {string} key - The unique identifier for the component
     * @returns {React.Component|Function|null} The registered component or null
     */
    get: (key) => {
        if (!registry.has(key)) {
            console.error(`PageRegistry: Component with key '${key}' not found in registry.`);
            return null;
        }
        return registry.get(key);
    },

    /**
     * Check if a component is registered
     * @param {string} key - The unique identifier for the component
     * @returns {boolean} True if the component is registered
     */
    has: (key) => registry.has(key),

    /**
     * Clear all registered components (useful for testing)
     */
    clear: () => registry.clear(),

    /**
     * Get all registered components
     * @returns {Map} A copy of the registry
     */
    getAll: () => new Map(registry)
};

export default PageRegistry;

